import { ValidationResponse } from "../../domain/entities/ValidationEntity";
import { API_BASE_URL, CORE_API_URL } from "../../../../config/api";
import { ourSupplyStateFor } from "../../../../shared/data/api/activeLocationsApi";

// HTTP Status Codes
const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

// API Error Class
class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

// Helper functions
const getStoredToken = () => {
  try {
    return localStorage.getItem("access_token");
  } catch (error) {
    console.error("Error getting stored token:", error);
    return null;
  }
};

const getStoredCompanyData = () => {
  try {
    const companyData = localStorage.getItem("company_data");
    return companyData ? JSON.parse(companyData) : null;
  } catch (error) {
    console.error("Error getting stored company data:", error);
    return null;
  }
};

// Get customer by ID (we'll need this for validation)
const getCustomerById = async (customerId: string) => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const response = await fetch(
    `${API_BASE_URL}/api/invoices/customers/${customerId}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new APIError(
      `Failed to fetch customer: ${response.statusText}`,
      response.status,
    );
  }

  return await response.json();
};

/**
 * Validates an invoice document against business rules
 * @param invoiceData - The invoice data to validate
 * @param invoiceDocument - The invoice document metadata
 * @param validationType - The type of validation: 'full_validation' or 'normal_validation'
 * @returns ValidationResponse with validation results
 */
export const validateInvoiceDocument = async (
  invoiceData: any,
  invoiceDocument: any,
  validationType: string = "full_validation",
): Promise<ValidationResponse> => {
  try {
    const token = getStoredToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    const companyProfile = getStoredCompanyData();
    // ZOUR-65 R17: OUR side of the supply is the state of the registration this
    // invoice was issued from, not the one state on the company row. Sending
    // the company state was raising "Source of supply does not match company
    // location" on every correct Bengaluru invoice — the company is in Tamil
    // Nadu, core resolved the document's location to Karnataka, and compared
    // the two. Falls back to the company state when there is no location to
    // ask, which is every single-registration company.
    const locationState = await ourSupplyStateFor(invoiceData);
    const source_of_supply = locationState ?? companyProfile.state;

    let isGstEnabled = true;
    if (companyProfile && companyProfile.companyGstEnabled === "0") {
      isGstEnabled = false;
    }

    // Get customer GST information
    let customer_gst = "";
    let destination_of_supply = "";
    if (invoiceData.customer_id) {
      const customer = await getCustomerById(invoiceData.customer_id);
      customer_gst = customer.data.gstin_vat_tax_id || "";
      destination_of_supply = customer.data.state || customer.data.country;
    }

    // Prepare request body
    const lineItemsWithTax = invoiceData.line_items.map((item: any) => ({
      ...item,
      // tax_amount: (item.item_total * (item.tax_percentage || 0)) / 100,
      tax_amount: isGstEnabled
        ? (item.item_total * (item.tax_percentage || 0)) / 100
        : 0,
    }));

    // Calculate total from line items
    // const calculatedTotal = lineItemsWithTax.reduce(
    //   (sum: number, item: any) =>
    //     sum + (item.item_total || 0) + (item.tax_amount || 0),
    //   0
    // );

    const requestBody = {
      bill_data: {
        seller_gst: invoiceData.vendor_gstin,
        buyer_gst: invoiceData.customer_gstin,
        company_gst: companyProfile.gstNumber ?? "",
        // ZOUR-65 R16: which GST registration issued this invoice, and the
        // pipeline's own match. THIS is the body the review page actually
        // sends — see the twin note in the bills validationApi.
        location_id: invoiceData.location_id ?? null,
        location_match: invoiceData.location_match ?? null,
        vendor_gst: customer_gst,
        source_of_supply: source_of_supply,
        destination_of_supply: destination_of_supply,
        vendor_id: invoiceData.customer_id,
        customer_id: invoiceData.customer_id, // engine reads customer_id for invoices (Override/k-NN party scope); vendor_id kept for other validators
        gst_type: invoiceDocument.gst_type,
        line_items: lineItemsWithTax,
        // total: calculatedTotal,
        total: invoiceData.total,
        bill_number: invoiceData.invoice_number,
        // export_lut_check keys the LUT validity window off the invoice date,
        // falling back to today when absent (#610)
        invoice_date: invoiceData.date || invoiceData.invoice_date || "",
        // ZOUR-19 (review 16): `date` as well as `invoice_date`. The GST-mode
        // resolver reads `date` first, and sending only one of the two left
        // the answer depending on which key this particular payload happened
        // to carry.
        date: invoiceData.date || invoiceData.invoice_date || "",
      },
      document_type: "invoice",
      job_id: invoiceDocument.jobid,
      validation_type: validationType,
    };

    // Make API call
    const response = await fetch(`${CORE_API_URL}/api_bill_validation.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new APIError(
        `Failed to validate document: ${response.statusText}`,
        response.status,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error validating invoice document:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};
